import type { UserPermissionsFetchResult } from "../../api/permissions/permissions.types";
import {
  activeTeamId as activeTeamIdKey,
  activeTeamIdForNewTabs,
} from "../../shared/constants/localstorage.constant";

const activeTeamUserOverrideKey = "activeTeamUserOverride";
const activeTeamContextKey = "activeTeamContext";
const teamAccessLostKey = "teamAccessLost";

type ActiveTeamContextMap = Record<string, string>;

export const PROFILE_LIST_PATH = "/protect";

export interface SetActiveTeamIdOptions {
  /** When true, session team wins over preferred login team for this tab. */
  isUserOverride?: boolean;
}

export const getActiveTeamId = (): string | null => {
  const storedTeamId = sessionStorage.getItem(activeTeamIdKey);
  if (storedTeamId?.length) {
    return storedTeamId;
  }

  // A new browser tab has a separate sessionStorage, so use the handoff
  // value written by the tab where the user selected the team.
  const crossTabTeamId = localStorage.getItem(activeTeamIdForNewTabs);
  return crossTabTeamId?.length ? crossTabTeamId : null;
};

export const hasActiveTeamUserOverride = (): boolean =>
  sessionStorage.getItem(activeTeamUserOverrideKey) === "1";

export const setActiveTeamId = (
  teamId: string,
  options?: SetActiveTeamIdOptions
): void => {
  sessionStorage.setItem(activeTeamIdKey, teamId);

  if (options?.isUserOverride) {
    localStorage.setItem(activeTeamIdForNewTabs, teamId);
    sessionStorage.setItem(activeTeamUserOverrideKey, "1");
  } else {
    sessionStorage.removeItem(activeTeamUserOverrideKey);
  }
};

/** Persists team override and navigates to the profile list so team-scoped data refetches. */
export const switchActiveTeamAndNavigateToProfileList = (
  teamId: string,
  teamName?: string
): void => {
  setActiveTeamId(teamId, { isUserOverride: true });
  if (teamName) {
    setLastKnownActiveTeamName(teamName, teamId);
  }
  globalThis.location.assign(PROFILE_LIST_PATH);
};

export const getLastKnownActiveTeamName = (teamId?: string): string | null => {
  const contextMap = readActiveTeamContextMap();
  const resolvedTeamId = teamId ?? getActiveTeamId();
  if (!resolvedTeamId) {
    return null;
  }

  return contextMap[resolvedTeamId] ?? null;
};

export const setLastKnownActiveTeamName = (
  name: string,
  teamId?: string
): void => {
  const resolvedTeamId = teamId ?? getActiveTeamId();
  if (!resolvedTeamId || !name) {
    return;
  }

  writeActiveTeamContextEntry(resolvedTeamId, name);
};

const readActiveTeamContextMap = (): ActiveTeamContextMap => {
  const storedContext = sessionStorage.getItem(activeTeamContextKey);
  if (!storedContext) {
    return {};
  }

  try {
    const parsed = JSON.parse(storedContext) as
      | ActiveTeamContextMap
      | { teamId?: string; teamName?: string };

    if (
      parsed &&
      typeof parsed === "object" &&
      "teamId" in parsed &&
      "teamName" in parsed &&
      parsed.teamId &&
      parsed.teamName
    ) {
      return { [parsed.teamId]: parsed.teamName };
    }

    return parsed as ActiveTeamContextMap;
  } catch {
    return {};
  }
};

const writeActiveTeamContextEntry = (
  teamId: string,
  teamName: string
): void => {
  const contextMap = readActiveTeamContextMap();
  contextMap[teamId] = teamName;
  sessionStorage.setItem(activeTeamContextKey, JSON.stringify(contextMap));
};

export const setTeamAccessLost = (lost: boolean): void => {
  if (typeof sessionStorage?.setItem !== "function") {
    return;
  }

  if (lost) {
    sessionStorage.setItem(teamAccessLostKey, "1");
    return;
  }

  sessionStorage.removeItem(teamAccessLostKey);
};

export const isTeamAccessLost = (): boolean =>
  sessionStorage.getItem(teamAccessLostKey) === "1";

export const clearActiveTeamId = (): void => {
  if (typeof sessionStorage?.removeItem !== "function") {
    return;
  }

  sessionStorage.removeItem(activeTeamIdKey);
  localStorage.removeItem(activeTeamIdForNewTabs);
  sessionStorage.removeItem(activeTeamUserOverrideKey);
  sessionStorage.removeItem(activeTeamContextKey);
  sessionStorage.removeItem(teamAccessLostKey);
};

/**
 * Seeds session storage from the preferred login team when the user has not
 * explicitly changed the team dropdown this session.
 */
export const seedPreferredLoginTeam = (
  preferredTeamId?: string | null
): void => {
  if (hasActiveTeamUserOverride()) {
    return;
  }

  const crossTabTeamId = localStorage.getItem(activeTeamIdForNewTabs);
  if (crossTabTeamId?.length) {
    setActiveTeamId(crossTabTeamId, { isUserOverride: true });
    return;
  }

  const preferredId =
    preferredTeamId && preferredTeamId.length > 0 ? preferredTeamId : null;

  if (preferredId) {
    setActiveTeamId(preferredId);
  }
};
/**
 * Resolves the active team for permissions state:
 * user session override → preferred login team → permissions team.
 * Seeds session from preferred login team only (never from permissions alone),
 * so a premature permissions seed cannot hide the user's preferred team.
 */
export interface ActiveTeamResolutionInput {
  permissionsTeamId: string | null;
  preferredTeamId: string | null;
}

/**
 * Resolves the team shown in team selectors (dropdown, preferred toggle).
 * Matches user override in session, then preferred login team, then session, then permissions.
 */
export const resolveCurrentTeamId = ({
  permissionsTeamId,
  preferredTeamId,
}: ActiveTeamResolutionInput): string | null => {
  const storedTeamId = getActiveTeamId();

  if (storedTeamId && hasActiveTeamUserOverride()) {
    return storedTeamId;
  }

  return preferredTeamId ?? storedTeamId ?? permissionsTeamId;
};

/**
 * Team id for team-scoped API calls and React Query cache keys.
 * Uses session storage (X-Team-Id source) with the same fallback as the UI when session is empty.
 */
export const resolveProfilesQueryTeamId = (
  input: ActiveTeamResolutionInput
): string | null => {
  return getActiveTeamId() ?? resolveCurrentTeamId(input);
};

export const canFetchTeamScopedProfiles = (
  isAuthorizationEnabled: boolean,
  isPermissionsLoaded: boolean,
  profilesQueryTeamId: string | null
): boolean => {
  if (!isAuthorizationEnabled) {
    return true;
  }

  return isPermissionsLoaded && Boolean(profilesQueryTeamId);
};

export const applyActiveTeamToPermissions = (
  permissionsData: UserPermissionsFetchResult,
  preferredTeamId?: string | null
): UserPermissionsFetchResult => {
  const storedTeamId = getActiveTeamId();
  const preferredId =
    preferredTeamId && preferredTeamId.length > 0 ? preferredTeamId : null;

  if (hasActiveTeamUserOverride() && storedTeamId) {
    return {
      ...permissionsData,
      teamId: storedTeamId,
    };
  }

  const resolvedTeamId = preferredId ?? storedTeamId ?? permissionsData.teamId;

  if (preferredId && storedTeamId !== preferredId && !hasActiveTeamUserOverride()) {
    setActiveTeamId(preferredId);
  }

  return {
    ...permissionsData,
    teamId: resolvedTeamId,
  };
};
